import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MapPin, MapPinOff, Navigation, CheckCircle2, Check } from 'lucide-react';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { useAssigneeTaskStatus } from '@/hooks/tasks/useAssigneeTaskStatus';
import { taskTypeOption } from '@/lib/tasks/taskTypes';
import { TaskCustomerActions } from '@/components/organisms/tasks/TaskCustomerActions';
import { useAuthTechnicianId } from '@/stores/auth.store';
import { useApiMutation } from '@/hooks/useApiMutation';
import {
  useMyDay,
  useTechLocationSharing,
  myDayQueryKey,
  type MyDayAppointment,
  type MyDayTask,
  type SharingStatus,
} from '@/hooks/tracking';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Switch } from '@/components/atoms/switch';
import { Skeleton } from '@/components/atoms/skeleton';

export const Route = createFileRoute('/_dashboard/dashboard/my-day')({
  component: MyDayPage,
});

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

function MyDayPage() {
  // FIX-myday — gate by the real technician link, not the role name, so an
  // owner-operator (non-"technician" role but linked to a technician) sees it.
  const technicianId = useAuthTechnicianId();
  const isLinked = technicianId != null;
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useMyDay(isLinked);

  const timezone = data?.timezone ?? null;
  const paused = data?.sharingPaused ?? false;
  const appointments = useMemo(() => data?.appointments ?? [], [data]);
  const tasks = useMemo(() => data?.tasks ?? [], [data]);
  const hasActive = appointments.some((a) => a.active);

  // The emitter runs only when the tech has a live appointment and hasn't paused.
  const { status } = useTechLocationSharing({
    enabled: isLinked && hasActive && !paused,
  });

  const pauseMutation = useApiMutation<unknown, boolean>({
    mutationFn: (nextPaused) =>
      apiPost(operationsClient, schedulerEndpoints.trackingPause(), {
        paused: nextPaused,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myDayQueryKey });
    },
  });

  const completeTaskMutation = useAssigneeTaskStatus();

  const arrivedMutation = useApiMutation<unknown, string>({
    mutationFn: (appointmentId) =>
      apiPost(operationsClient, schedulerEndpoints.trackingArrived(appointmentId)),
    onSuccess: () => {
      toast.success('Marked on-site — the customer has been notified.');
      void queryClient.invalidateQueries({ queryKey: myDayQueryKey });
    },
  });

  if (!isLinked || isError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="text-xl font-semibold text-foreground">My Day</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This view is for field technicians. Your account isn’t linked to a technician profile.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-foreground">My Day</h1>
        <p className="text-sm text-muted-foreground">
          Your appointments and tasks for today
          {timezone ? ` · ${formatTimeZoneShort(timezone)}` : ''}
        </p>
      </header>

      <SharingCard
        paused={paused}
        status={status}
        hasActive={hasActive}
        toggling={pauseMutation.isPending}
        lastSharedAt={data?.lastSharedAt ?? null}
        timezone={timezone}
        onToggle={(nextPaused) => pauseMutation.mutate(nextPaused)}
      />

      <div className="mt-5 space-y-3">
        {isLoading ? (
          <>
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </>
        ) : appointments.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No appointments scheduled for today.
            </CardContent>
          </Card>
        ) : (
          appointments.map((appt) => (
            <AppointmentRow
              key={appt.id}
              appt={appt}
              timezone={timezone}
              arriving={arrivedMutation.isPending && arrivedMutation.variables === appt.id}
              onArrived={() => arrivedMutation.mutate(appt.id)}
            />
          ))
        )}
      </div>

      {tasks.length > 0 && (
        <section className="mt-8" aria-labelledby="my-day-tasks">
          <h2 id="my-day-tasks" className="mb-3 text-sm font-semibold text-foreground">
            Tasks <span className="font-normal text-muted-foreground">{tasks.length}</span>
          </h2>
          <div className="space-y-3">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                timezone={timezone}
                completing={
                  completeTaskMutation.isPending &&
                  completeTaskMutation.variables?.taskId === task.id
                }
                onComplete={() => completeTaskMutation.mutate({ taskId: task.id, status: 'done' })}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * One task on the tech's day. No job, no invoice — just the customer, what to
 * do, and the buttons to do it: call or text, plus directions when the task
 * names an address.
 */
function TaskRow({
  task,
  timezone,
  completing,
  onComplete,
}: {
  task: MyDayTask;
  timezone: string | null;
  completing: boolean;
  onComplete: () => void;
}) {
  const option = taskTypeOption(task.type);
  const Icon = option.icon;
  // Only a timed task has an instant to render in the business zone; an
  // all-day task's due is a date, which a zone conversion would shift.
  const dueAt = task.allDay ? null : task.dueDate;
  const when = dueAt ? formatDateTimeInTimeZone(dueAt, timezone ?? undefined, TIME_OPTS) : null;
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Icon className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">{task.title}</p>
            <p className="truncate text-sm text-muted-foreground">
              {option.label} · {task.contact.name}
              {when ? ` · ${when}` : ''}
              {when && task.durationMinutes ? ` (${task.durationMinutes} min)` : ''}
            </p>
            {task.address ? (
              <a
                className="mt-1 inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
                href={`https://maps.google.com/?q=${encodeURIComponent(task.address)}`}
                target="_blank"
                rel="noreferrer"
              >
                <Navigation className="size-3.5" aria-hidden="true" />
                {task.address}
              </a>
            ) : null}
          </div>
          {task.overdue ? (
            <Badge variant="destructive" className="shrink-0">
              Overdue
            </Badge>
          ) : null}
        </div>
        {task.description ? (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{task.description}</p>
        ) : null}
        <TaskCustomerActions
          contactId={task.contact.id}
          phone={task.contact.phone}
          type={task.type}
          size="sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2"
          disabled={completing}
          onClick={onComplete}
        >
          <Check className="size-4" aria-hidden="true" />
          {completing ? 'Saving…' : 'Mark done'}
        </Button>
      </CardContent>
    </Card>
  );
}

function SharingCard({
  paused,
  status,
  hasActive,
  toggling,
  lastSharedAt,
  timezone,
  onToggle,
}: {
  paused: boolean;
  status: SharingStatus;
  hasActive: boolean;
  toggling: boolean;
  lastSharedAt: string | null;
  timezone: string | null;
  onToggle: (nextPaused: boolean) => void;
}) {
  const sharingOn = !paused && hasActive;
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-4 py-4">
        <div className="flex items-start gap-3">
          <span
            className={sharingOn ? 'mt-0.5 text-primary' : 'mt-0.5 text-muted-foreground'}
            aria-hidden
          >
            {sharingOn ? <MapPin className="h-5 w-5" /> : <MapPinOff className="h-5 w-5" />}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">Location sharing</span>
              <Badge variant={sharingOn ? 'default' : 'secondary'}>
                {paused ? 'Paused' : !hasActive ? 'Standby' : sharingStatusLabel(status)}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {sharingHint({
                paused,
                hasActive,
                status,
                lastSharedAt,
                timezone,
              })}
            </p>
          </div>
        </div>
        <Switch
          checked={!paused}
          disabled={toggling}
          onCheckedChange={(checked) => onToggle(!checked)}
          aria-label="Toggle location sharing"
        />
      </CardContent>
    </Card>
  );
}

function AppointmentRow({
  appt,
  timezone,
  arriving,
  onArrived,
}: {
  appt: MyDayAppointment;
  timezone: string | null;
  arriving: boolean;
  onArrived: () => void;
}) {
  const start = formatDateTimeInTimeZone(appt.scheduledStart, timezone, TIME_OPTS);
  const end = formatDateTimeInTimeZone(appt.scheduledEnd, timezone, TIME_OPTS);
  const canArrive = appt.active && !appt.arrivedAt && !appt.completedAt;

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-foreground">
                {appt.customerName || 'Appointment'}
              </span>
              <Badge variant="outline">{prettyStatus(appt.status)}</Badge>
            </div>
            {(start || end) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {start}
                {end ? ` – ${end}` : ''}
              </p>
            )}
            {/* §11.4 — show the customer's promised window alongside the precise
                start above, so the tech knows what the customer was told. */}
            {appt.arrivalWindow && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Customer window:{' '}
                {formatDateTimeInTimeZone(appt.arrivalWindow.start, timezone, TIME_OPTS)} –{' '}
                {formatDateTimeInTimeZone(appt.arrivalWindow.end, timezone, TIME_OPTS)}
              </p>
            )}
            {appt.address && (
              <p className="mt-1 truncate text-xs text-muted-foreground">{appt.address}</p>
            )}
            {appt.arrivedAt && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-primary">
                <CheckCircle2 className="h-3.5 w-3.5" /> On-site
              </p>
            )}
          </div>
          {canArrive && (
            <Button size="sm" disabled={arriving} onClick={onArrived}>
              <Navigation className="mr-1.5 h-4 w-4" />
              I’ve arrived
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function sharingStatusLabel(status: SharingStatus): string {
  switch (status) {
    case 'sharing':
      return 'On';
    case 'acquiring':
      return 'Locating…';
    case 'denied':
      return 'Off';
    case 'unavailable':
      return 'Unavailable';
    default:
      return 'Off';
  }
}

function sharingHint({
  paused,
  hasActive,
  status,
  lastSharedAt,
  timezone,
}: {
  paused: boolean;
  hasActive: boolean;
  status: SharingStatus;
  lastSharedAt: string | null;
  timezone: string | null;
}): string {
  if (paused) return 'Paused — customers won’t see your live ETA.';
  if (!hasActive) return 'Sharing starts automatically when you’re dispatched to a job.';
  if (status === 'denied') return 'Sharing off — location permission denied in your browser.';
  if (status === 'unavailable') return 'Your browser doesn’t support location sharing.';
  if (status === 'acquiring') return 'Getting your location…';
  if (lastSharedAt) {
    const at = formatDateTimeInTimeZone(lastSharedAt, timezone, TIME_OPTS);
    return at ? `Live — last shared ${at}.` : 'Live — sharing your location.';
  }
  return 'Live — sharing your location with the customer.';
}

function prettyStatus(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatTimeZoneShort(tz: string): string {
  const parts = tz.split('/');
  return (parts[parts.length - 1] ?? tz).replace(/_/g, ' ');
}
